import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { TenantWorkspaceApi, type BusinessProfile, type BusinessProfilePatch } from '@/modules/tenants/services/workspace-api';
import { Input } from '@/shared/ui/input';
import { Button } from '@/shared/ui/button';
import { ErrorState } from '@/shared/feedback';
import { FiSave, FiRefreshCw, FiAlertCircle, FiPackage, FiGlobe, FiMail } from 'react-icons/fi';

interface BusinessInfoPageProps {
  initialProfile?: BusinessProfile;
}

export function BusinessInfoPage({ initialProfile }: BusinessInfoPageProps = {}): ReactNode {
  const [profile, setProfile] = useState<BusinessProfile | null>(initialProfile || null);
  const [isLoading, setIsLoading] = useState(!initialProfile);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const errorMessage = error?.message ?? 'An unknown error occurred';
  const [formData, setFormData] = useState<BusinessProfilePatch>({});
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (!initialProfile) {
      loadProfile();
    }
  }, [initialProfile]);

  const loadProfile = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await TenantWorkspaceApi.getProfile();
      setProfile(data);
      setFormData({});
      setHasChanges(false);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load profile'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field: keyof BusinessProfilePatch, value: unknown) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleContactVisibilityChange = (key: string, value: boolean) => {
    setFormData(prev => ({
      ...prev,
      contactVisibility: { ...prev.contactVisibility, [key]: value },
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!hasChanges) return;
    setIsSaving(true);
    setError(null);
    try {
      const updated = await TenantWorkspaceApi.updateProfile(formData);
      setProfile(updated);
      setFormData({});
      setHasChanges(false);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to save profile'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleRefresh = () => {
    setFormData({});
    setHasChanges(false);
    loadProfile();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <ErrorState title="Could not load business information" message={error.message} onRetry={loadProfile} />
    );
  }

  if (!profile) {
    return (
      <ErrorState title="No profile found" message="This tenant does not have a business profile yet." />
    );
  }

  const contactVisibilityKeys = [
    'primary_email',
    'business_phone',
    'whatsapp_number',
    'support_email',
    'alternate_contact',
  ] as const;

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold">Business Information</h2>
          <p className="text-sm text-content-muted mt-1">
            Core business identity and contact details. Changes are saved via PATCH /api/v1/tenants/current/profile.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={handleRefresh} disabled={isLoading}>
            <FiRefreshCw className="h-4 w-4 mr-2" />
            Reset
          </Button>
          <Button onClick={handleSave} disabled={!hasChanges || isSaving} loading={isSaving}>
            <FiSave className="h-4 w-4 mr-2" />
            Save Changes
          </Button>
        </div>
      </div>

      {error && (
        <ErrorState title="Save failed" message={errorMessage} />
      )}

      {/* Identity Section */}
      <section className="rounded-lg border border-border p-6 space-y-6">
        <h3 className="text-lg font-medium flex items-center gap-2">
          <FiPackage className="h-5 w-5" />
          Business Identity
        </h3>

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Public Name *"
            value={formData.publicName ?? profile.publicName}
            onChange={(e) => handleChange('publicName', e.currentTarget.value)}
            required
            maxLength={120}
            placeholder="Customer-facing business name"
          />
          <Input
            label="Legal Name"
            value={formData.legalName ?? profile.legalName ?? ''}
            onChange={(e) => handleChange('legalName', e.currentTarget.value || null)}
            maxLength={200}
            placeholder="Legal entity name"
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Business Type"
            value={formData.businessType ?? profile.businessType ?? ''}
            onChange={(e) => handleChange('businessType', e.currentTarget.value || null)}
            maxLength={100}
            placeholder="e.g., Restaurant, Salon, Bakery"
          />
          <Input
            label="Primary Category"
            value={formData.primaryCategory ?? profile.primaryCategory ?? ''}
            onChange={(e) => handleChange('primaryCategory', e.currentTarget.value || null)}
            maxLength={100}
            placeholder="e.g., Fine Dining, Hair Salon, Artisan Bakery"
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Establishment Year"
            type="number"
            value={formData.establishmentYear ?? profile.establishmentYear ?? ''}
            onChange={(e) => handleChange('establishmentYear', e.currentTarget.value ? parseInt(e.currentTarget.value, 10) : null)}
            min={1800}
            max={new Date().getFullYear()}
            placeholder="YYYY"
          />
          <Input
            label="Website URL"
            type="url"
            value={formData.websiteUrl ?? profile.websiteUrl ?? ''}
            onChange={(e) => handleChange('websiteUrl', e.currentTarget.value || null)}
            placeholder="https://example.com"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-content mb-1">Description</label>
          <textarea
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-content placeholder:text-content-muted focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent min-h-[80px] resize-y"
            value={formData.description ?? profile.description ?? ''}
            onChange={(e) => handleChange('description', e.currentTarget.value || null)}
            maxLength={2000}
            placeholder="Describe your business..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-content mb-1">Registration Status</label>
          <select
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-content focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            value={formData.registrationStatus ?? profile.registrationStatus ?? ''}
            onChange={(e) => handleChange('registrationStatus', e.currentTarget.value || null)}
          >
            <option value="">Select status</option>
            <option value="unregistered">Unregistered</option>
            <option value="pending">Pending</option>
            <option value="registered">Registered</option>
          </select>
        </div>
      </section>

      {/* Contact Information Section */}
      <section className="rounded-lg border border-border p-6 space-y-6">
        <h3 className="text-lg font-medium flex items-center gap-2">
          <FiMail className="h-5 w-5" />
          Contact Information
        </h3>

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Primary Email"
            type="email"
            value={formData.primaryEmail ?? profile.primaryEmail ?? ''}
            onChange={(e) => handleChange('primaryEmail', e.currentTarget.value || null)}
            placeholder="contact@business.com"
          />
          <Input
            label="Business Phone"
            value={formData.businessPhone ?? profile.businessPhone ?? ''}
            onChange={(e) => handleChange('businessPhone', e.currentTarget.value || null)}
            placeholder="+91 98765 43210"
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="WhatsApp Number"
            value={formData.whatsappNumber ?? profile.whatsappNumber ?? ''}
            onChange={(e) => handleChange('whatsappNumber', e.currentTarget.value || null)}
            placeholder="+91 98765 43210"
          />
          <Input
            label="Support Email"
            type="email"
            value={formData.supportEmail ?? profile.supportEmail ?? ''}
            onChange={(e) => handleChange('supportEmail', e.currentTarget.value || null)}
            placeholder="support@business.com"
          />
        </div>

        <Input
          label="Alternate Contact"
          value={formData.alternateContact ?? profile.alternateContact ?? ''}
          onChange={(e) => handleChange('alternateContact', e.currentTarget.value || null)}
          placeholder="Additional contact info"
        />

        {/* Contact Visibility */}
        <div className="pt-4 border-t border-border">
          <h4 className="text-sm font-medium mb-3">Contact Visibility</h4>
          <p className="text-sm text-content-muted mb-3">
            Control which contact methods are visible to customers on your public profile.
          </p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {contactVisibilityKeys.map((key) => {
              const label = key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
              const currentValue = Boolean(formData.contactVisibility?.[key] ?? profile.contactVisibility[key] ?? false);
              return (
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={currentValue}
                    onChange={(e) => handleContactVisibilityChange(key, e.currentTarget.checked)}
                    className="rounded border-border text-primary focus:ring-primary"
                  />
                  <span className="text-sm text-content">{label}</span>
                </label>
              );
            })}
          </div>
        </div>
      </section>

      {/* Current Values Display */}
      <details className="rounded-lg border border-border p-6">
        <summary className="cursor-pointer font-medium text-content">Current Saved Values (Read-Only)</summary>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 text-sm">
          <ProfileField label="Version" value={String(profile.version)} />
          <ProfileField label="Banner Document" value={profile.bannerDocumentId ? `${profile.bannerDocumentId.slice(0, 8)}…` : '—'} />
          <ProfileField label="Primary Color" value={profile.primaryColor || '—'} />
          <ProfileField label="Tenant ID" value={profile.tenantId} />
        </div>
      </details>
    </div>
  );
}

function ProfileField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-content-muted uppercase tracking-wide">{label}</dt>
      <dd className="text-sm text-content font-mono">{value}</dd>
    </div>
  );
}